/**
 * Settings hub: workspace actions + feedback.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useSession } from '@/features/auth/hooks/useSession';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { isPaidTier } from '@/features/billing/lib/tier-predicates';
import { useFeedback } from '@/app/providers/FeedbackContext';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useIsCompactLayout } from '@/common/hooks';
import { apiFetch } from '@/lib/apiClient';
import { env } from '@/lib/env';
import { DemoDataMenuItem } from './DemoDataMenuItem';

interface WorkspaceOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewArchived?: () => void;
  onOpenProfile?: () => void;
  onOpenNotifications?: () => void;
}

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 8px',
  background: 'none',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  color: 'var(--color-text-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  textAlign: 'left',
};

export function WorkspaceOptionsModal({ isOpen, onClose, onViewArchived, onOpenProfile, onOpenNotifications }: WorkspaceOptionsModalProps) {
  const { isReadOnly, clearSession, profile, selectedContainer, token } = useSession();
  const [loggingOut, setLoggingOut] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const { openModal: openFeedback } = useFeedback();
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  useEscapeKey(onClose, isOpen);
  const isCompact = useIsCompactLayout();
  const workspaceTier = useWorkspacePlanTier();
  const isOwner = selectedContainer?.isOwner !== false;
  const tierKnown = workspaceTier !== null;
  // `trial` is unpaid (PDR 011 §7 / §9a): a trial owner sees the "Upgrade to Pro"
  // CTA below (not "Manage billing"), exactly like a free owner.
  const hasActiveSubscription = isPaidTier(workspaceTier);
  // Backstop (#3368): a platform-level paid account must never see the upgrade
  // CTA even if the per-workspace tier query transiently degrades to 'free'.
  // Mirrors the Sidebar gate (profile.isPaidAccount).
  const isPaidAccount = profile?.isPaidAccount === true;

  if (!isOpen) return null;

  const portalRoot = document.getElementById('modal-portal-root');



  const handleViewArchived = () => {
    onClose();
    if (onViewArchived) {
      onViewArchived();
    } else {
      window.dispatchEvent(new CustomEvent('openArchivedModal'));
    }
  };

  const handleImportExport = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('openDataBackupsModal'));
  };

  const handleFeedback = () => {
    onClose();
    openFeedback();
  };

  const modalContent = (
    <div
      className="workspace-options-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Workspace options"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalContentRef}
        className="xv-scroll"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          // Sits above the rail's profile avatar. Nordic/Atelier float the rail
          // 16px up (avatar with it), so they raise this so the popup clears the
          // avatar instead of touching it. (var on <html> via [data-theme=…].)
          bottom: isCompact ? 0 : 'var(--account-popup-bottom, 62px)',
          // 3px gap from the (flush) sidebar rail by default; Nordic/Atelier float
          // the rail with a 16px left margin, so they override this to 19px so the
          // popup still sits inside the rail. (var on <html> via [data-theme=…].)
          left: isCompact ? 0 : 'var(--account-popup-left, 3px)',
          backgroundColor: 'var(--color-surface, white)',
          borderRadius: isCompact ? '12px 12px 0 0' : '12px',
          /* Atelier overrides --popover-shadow with a stronger dark shadow so the
             menu reads over the dark walnut desk; other themes use the fallback. */
          boxShadow: 'var(--popover-shadow, 0 4px 24px rgba(0, 0, 0, 0.18))',
          /* Fits within the expanded sidebar rail (--rail-expanded-width: 220px,
             SidebarRail.css) with symmetric 3px gaps: 3px left + 214px + 3px right
             = 220, so it never pokes into the module beside it. */
          width: isCompact ? '100%' : '214px',
          maxHeight: isCompact ? '80vh' : 'calc(100vh - 80px)',
          overflow: 'auto',
        }}
      >

        <div style={{ padding: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {!isReadOnly && (
              <button
                onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('openTeamModal')); }}
                style={btnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Settings
              </button>
            )}
            <button
              onClick={() => { onClose(); onOpenProfile?.(); }}
              style={btnStyle}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Profile
            </button>
            {/* "Workspace settings" (gear → /settings full-page) removed — the
                "Workspace" modal above (TeamModal) is the single workspace-settings
                surface (#3442). The /settings route is being retired separately. */}
            <button
              onClick={handleFeedback}
              style={btnStyle}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
              Send feedback
            </button>
            {/* Upgrade — free-tier workspace owners only. Hidden while tier is
                loading and for platform-level paid accounts (#3368). */}
            {tierKnown && !hasActiveSubscription && !isPaidAccount && isOwner && (
              <button
                onClick={() => {
                  onClose();
                  window.dispatchEvent(new CustomEvent('xavior:open-upgrade-modal'));
                }}
                style={{ ...btnStyle, color: 'var(--color-primary, #0EA5E9)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                Upgrade plan
              </button>
            )}
            {/* Manage subscription — workspace has active paid tier + user is owner. */}
            {tierKnown && hasActiveSubscription && isOwner && (
              <button
                onClick={async () => {
                  // Native iOS/Android Capacitor shell only — opening Stripe inside the webview traps the user.
                  if (Capacitor.isNativePlatform()) {
                    setPortalError('Please manage your subscription on the web. Mobile billing portal is coming soon.');
                    return;
                  }
                  if (!token || openingPortal) return;
                  setOpeningPortal(true);
                  setPortalError(null);
                  try {
                    const res = await apiFetch<{ url?: string }>('payment/portal', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ returnUrl: window.location.href }),
                      authToken: token,
                    });
                    if (res?.url) {
                      window.location.href = res.url;
                      return;
                    }
                    setPortalError('Could not open billing portal. Please try again.');
                  } catch (err) {
                    const message = err instanceof Error ? err.message : 'Failed to open billing portal.';
                    setPortalError(message);
                  } finally {
                    setOpeningPortal(false);
                  }
                }}
                disabled={openingPortal}
                style={btnStyle}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10H22" />
                </svg>
                {openingPortal ? 'Opening…' : 'Manage billing'}
              </button>
            )}
            {portalError && (
              <p style={{ margin: '4px 12px 8px', fontSize: 12, color: 'var(--color-danger, #EA4335)' }}>{portalError}</p>
            )}
            {/* Notifications — push permissions only matter on mobile/native.
                On desktop the underlying MobileSettingsModal has nothing
                actionable to offer. Gate to compact (catches mobile web +
                native mobile). */}
            {onOpenNotifications && isCompact && (
              <button
                onClick={() => { onClose(); onOpenNotifications(); }}
                style={btnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Notifications
              </button>
            )}
            <button
              onClick={handleViewArchived}
              style={btnStyle}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              Archived
            </button>
            {/* Demo data toggle — moved here from the header (#demo-move). */}
            <DemoDataMenuItem onAfterAction={onClose} />
          </div>
        </div>

        <div style={{ padding: '0 8px' }}>
          <div style={{ borderTop: '1px solid var(--color-border)', margin: '0 4px' }} />
        </div>

        <div style={{ padding: '8px' }}>
          <button
            onClick={async () => {
              setLoggingOut(true);
              try {
                await apiFetch('/auth/logout', { method: 'POST' });
              } catch {
                // continue with local logout
              }
              clearSession();
              onClose();
            }}
            style={btnStyle}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            disabled={loggingOut}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>

        <div style={{ padding: '0 8px' }}>
          <div style={{ borderTop: '1px solid var(--color-border)', margin: '0 4px' }} />
        </div>

        <div style={{
          padding: '10px 8px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
          <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <a href={env.privacyUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Privacy Policy</a>
            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'var(--color-text-muted)', opacity: 0.5 }} />
            <a href={env.termsUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Terms</a>
          </div>
          <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', opacity: 0.6, lineHeight: 1.4 }}>
            XAVIOR LLC &middot; Patent Pending: App. No. 63/919,350
          </div>
        </div>
      </div>
    </div>
  );

  if (portalRoot) {
    return createPortal(modalContent, portalRoot);
  }
  return modalContent;
}
